import Fuse from 'fuse.js';
import map from 'lodash/map';

import { IUFuseCache } from '../lib/ContactsDatabase';
import { hasContactId, TAppContactWithId } from '../core/libs/utils';

import { TAppContact } from 'TProtocol/common/models';

export enum USearchItemField {
  contactId = 1,
  fullName,
  primaryDisplayNumber,
  primaryPhoneNumber,
  personalMobileDisplayNumber,
  personalMobilePhoneNumber,
  personalLandlineDisplayNumber,
  personalLandlinePhoneNumber,
  personalEmailDisplayEmail,
  professionalMobileDisplayNumber,
  professionalLandlineDisplayNumber,
  professionalEmailDisplayEmail,
  personalThirdPartyIds,
  professionalThirdPartyIds,
  primaryProfessionalInfoJobTitle,
  primaryProfessionalInfoDepartmentName,
  primaryProfessionalInfoCompanyName,
  otherPhonesDisplayNumber,
  otherPhonesPhoneNumber,
  otherEmailsDisplayEmail,
  personalAddressAddress1,
  personalAddressAddress2,
  personalAddressCity,
  personalAddressAdmin1,
  personalAddressPostalCode,
  personalAddressCountry,
  professionalAddressAddress1,
  professionalAddressAddress2,
  professionalAddressCity,
  professionalAddressAdmin1,
  professionalAddressPostalCode,
  professionalAddressCountry,
  otherAddressAddress1,
  otherAddressAddress2,
  otherAddressCity,
  otherAddressAdmin1,
  otherAddressPostalCode,
  otherAddressCountry,
}

export type IUSearchItem = {
  [key in USearchItemField]: string | undefined;
}

const appContactToSearchItem = (contact: TAppContact): IUSearchItem => {
  return {
    [USearchItemField.contactId]: contact.contactId,
    [USearchItemField.fullName]: contact.name?.fullName,
    [USearchItemField.primaryDisplayNumber]: contact.primaryMobile?.displayNumber,
    [USearchItemField.primaryPhoneNumber]: contact.primaryMobile?.phoneNumber,
    [USearchItemField.personalMobileDisplayNumber]: contact.personalMobile?.displayNumber,
    [USearchItemField.personalMobilePhoneNumber]: contact.personalMobile?.phoneNumber,
    [USearchItemField.personalLandlineDisplayNumber]: contact.personalLandline?.displayNumber,
    [USearchItemField.personalLandlinePhoneNumber]: contact.personalLandline?.phoneNumber,
    [USearchItemField.personalEmailDisplayEmail]: contact.personalEmail?.displayEmail,
    [USearchItemField.professionalMobileDisplayNumber]: contact.professionalMobile?.displayNumber,
    [USearchItemField.professionalLandlineDisplayNumber]: contact.professionalLandline?.displayNumber,
    [USearchItemField.professionalEmailDisplayEmail]: contact.professionalEmail?.displayEmail,
    [USearchItemField.personalThirdPartyIds]: map(contact.personalThirdPartyIds, 'value').join(' '),
    [USearchItemField.professionalThirdPartyIds]: map(contact.professionalThirdPartyIds, 'value').join(' '),
    [USearchItemField.primaryProfessionalInfoJobTitle]: contact.primaryProfessionalInfo?.jobTitle,
    [USearchItemField.primaryProfessionalInfoDepartmentName]: contact.primaryProfessionalInfo?.departmentName,
    [USearchItemField.primaryProfessionalInfoCompanyName]: contact.primaryProfessionalInfo?.companyName,
    [USearchItemField.otherPhonesDisplayNumber]: map(contact.otherPhones, 'displayNumber').join(' '),
    [USearchItemField.otherPhonesPhoneNumber]: map(contact.otherPhones, 'phoneNumber').join(' '),
    [USearchItemField.otherEmailsDisplayEmail]: contact.otherEmails?.join(' '),
    [USearchItemField.personalAddressAddress1]: contact.personalAddress?.address1,
    [USearchItemField.personalAddressAddress2]: contact.personalAddress?.address2,
    [USearchItemField.personalAddressCity]: contact.personalAddress?.city,
    [USearchItemField.personalAddressAdmin1]: contact.personalAddress?.admin1,
    [USearchItemField.personalAddressPostalCode]: contact.personalAddress?.postalCode,
    [USearchItemField.personalAddressCountry]: contact.personalAddress?.country,
    [USearchItemField.professionalAddressAddress1]: contact.professionalAddress?.address1,
    [USearchItemField.professionalAddressAddress2]: contact.professionalAddress?.address2,
    [USearchItemField.professionalAddressCity]: contact.professionalAddress?.city,
    [USearchItemField.professionalAddressAdmin1]: contact.professionalAddress?.admin1,
    [USearchItemField.professionalAddressPostalCode]: contact.professionalAddress?.postalCode,
    [USearchItemField.professionalAddressCountry]: contact.professionalAddress?.country,
    [USearchItemField.otherAddressAddress1]: map(contact.otherAddresses, 'address1').join(' '),
    [USearchItemField.otherAddressAddress2]: map(contact.otherAddresses, 'address2').join(' '),
    [USearchItemField.otherAddressCity]: map(contact.otherAddresses, 'city').join(' '),
    [USearchItemField.otherAddressAdmin1]: map(contact.otherAddresses, 'admin1').join(' '),
    [USearchItemField.otherAddressPostalCode]: map(contact.otherAddresses, 'postalCode').join(' '),
    [USearchItemField.otherAddressCountry]: map(contact.otherAddresses, 'country').join(' ')
  };
};

const Search = (() => {
  let fuse: Fuse<IUSearchItem> | undefined;
  const entryMap = new Map<string, IUSearchItem>();

  // Exclude contactId, and make fullName more heavily weighted.
  const weightedKeys = [
    {
      name: `${USearchItemField.fullName}`,
      weight: 5
    },
    `${USearchItemField.primaryDisplayNumber}`,
    `${USearchItemField.primaryPhoneNumber}`,
    `${USearchItemField.personalMobileDisplayNumber}`,
    `${USearchItemField.personalMobilePhoneNumber}`,
    `${USearchItemField.personalLandlineDisplayNumber}`,
    `${USearchItemField.personalLandlinePhoneNumber}`,
    `${USearchItemField.personalEmailDisplayEmail}`,
    `${USearchItemField.professionalMobileDisplayNumber}`,
    `${USearchItemField.professionalLandlineDisplayNumber}`,
    `${USearchItemField.professionalEmailDisplayEmail}`,
    `${USearchItemField.personalThirdPartyIds}`,
    `${USearchItemField.professionalThirdPartyIds}`,
    `${USearchItemField.primaryProfessionalInfoJobTitle}`,
    `${USearchItemField.primaryProfessionalInfoDepartmentName}`,
    `${USearchItemField.primaryProfessionalInfoCompanyName}`,
    `${USearchItemField.otherPhonesDisplayNumber}`,
    `${USearchItemField.otherPhonesPhoneNumber}`,
    `${USearchItemField.otherEmailsDisplayEmail}`,
    `${USearchItemField.personalAddressAddress1}`,
    `${USearchItemField.personalAddressAddress2}`,
    `${USearchItemField.personalAddressCity}`,
    `${USearchItemField.personalAddressAdmin1}`,
    `${USearchItemField.personalAddressPostalCode}`,
    `${USearchItemField.personalAddressCountry}`,
    `${USearchItemField.professionalAddressAddress1}`,
    `${USearchItemField.professionalAddressAddress2}`,
    `${USearchItemField.professionalAddressCity}`,
    `${USearchItemField.professionalAddressAdmin1}`,
    `${USearchItemField.professionalAddressPostalCode}`,
    `${USearchItemField.professionalAddressCountry}`,
    `${USearchItemField.otherAddressAddress1}`,
    `${USearchItemField.otherAddressAddress2}`,
    `${USearchItemField.otherAddressCity}`,
    `${USearchItemField.otherAddressAdmin1}`,
    `${USearchItemField.otherAddressPostalCode}`,
    `${USearchItemField.otherAddressCountry}`
  ];

  const options: Fuse.IFuseOptions<IUSearchItem> = {
    keys: weightedKeys,
    shouldSort: true,
    includeMatches: true,
    threshold: 0.1,
    ignoreLocation: true,
  };

  const initialized = (): boolean => {
    return !!fuse;
  }

  const createFromCache = (cache: IUFuseCache) => {
    fuse = new Fuse(cache.contacts, options, cache.index);

    cache.contacts.forEach((si: IUSearchItem) => {
      const contactId = si[USearchItemField.contactId];
      if (contactId) {
        entryMap.set(contactId, si);
      }
    })
  };

  const updateSearch = (contacts: TAppContact[]) => {
    const list = contacts.map(appContactToSearchItem);
    fuse = new Fuse(list, options);
  };

  const addItem = (contact: TAppContactWithId): IUSearchItem => {
    const contactId = contact.contactId;

    const searchItem = appContactToSearchItem(contact);
    if (entryMap.get(contactId)) {
      fuse?.remove((c) => c[USearchItemField.contactId] === contactId);
    }
    entryMap.set(contactId, searchItem);

    if (!fuse) {
      updateSearch([contact]);
    } else {
      fuse.add(searchItem);
    }

    return searchItem;
  };

  const removeItem = (contactId: string) => {
    if (entryMap.get(contactId)) {
      fuse?.remove((c) => c[USearchItemField.contactId] === contactId);
      entryMap.delete(contactId);
    }
  };

  const updateItem = (contact: TAppContact) => {
    if (hasContactId(contact)) {
      removeItem(contact.contactId);
      addItem(contact);
    }
  };

  const getContactsForQuery = (query: string): Fuse.FuseResult<IUSearchItem>[] => {
    if (fuse === undefined) {
      return [];
    }
    return fuse.search(query);
  };

  const getIndex = () => {
    return fuse?.getIndex();
  };

  const clear = () => {
    fuse = undefined;
  }

  return {
    initialized,
    createFromCache,
    updateSearch,
    addItem,
    removeItem,
    updateItem,
    getContactsForQuery,
    getIndex,
    clear
  };

})();

export default Search;
